import React, { FC, useState } from "react";
import { motion } from "framer-motion";
import {
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  DocumentIcon,
  CalendarIcon,
} from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";
import { Location } from "data/types";
import homeIcon from "../../images/home-icon.jpg";
import SimpleAvailabilityModal from "./SimpleAvailabilityModal";

interface ModernSpaceCardProps {
  location: Location;
  isCustomer?: boolean;
  onDelete?: (id: string) => void;
}

const ModernSpaceCard: FC<ModernSpaceCardProps> = ({ location, isCustomer = false, onDelete }) => {
  const [isAvailabilityModalOpen, setIsAvailabilityModalOpen] = useState(false);

  const getStatusConfig = (validation: string) => {
    switch (validation) {
      case "approuvé":
        return {
          color: "text-emerald-700 dark:text-emerald-400",
          bgColor: "bg-emerald-100 dark:bg-emerald-900/30",
          icon: CheckCircleIcon,
          label: "Approuvé",
        };
      case "en attente":
        return {
          color: "text-orange-700 dark:text-orange-400",
          bgColor: "bg-orange-100 dark:bg-orange-900/30",
          icon: ClockIcon,
          label: "En attente",
        };
      case "bloqué":
        return {
          color: "text-red-700 dark:text-red-400",
          bgColor: "bg-red-100 dark:bg-red-900/30",
          icon: ExclamationTriangleIcon,
          label: "Bloqué",
        };
      case "brouillon":
        return {
          color: "text-gray-700 dark:text-gray-400",
          bgColor: "bg-gray-100 dark:bg-gray-900/30",
          icon: DocumentIcon,
          label: "Brouillon",
        };
      case "en cours":
        return {
          color: "text-blue-700 dark:text-blue-400",
          bgColor: "bg-blue-100 dark:bg-blue-900/30",
          icon: ClockIcon,
          label: "En cours",
        };
      default:
        return {
          color: "text-gray-700 dark:text-gray-400",
          bgColor: "bg-gray-100 dark:bg-gray-900/30",
          icon: DocumentIcon,
          label: validation || "Inconnu",
        };
    }
  };

  const statusConfig = getStatusConfig(location.validation || "");
  const StatusIcon = statusConfig.icon;

  const getEditLink = () => {
    if (location.validation === "brouillon") {
      return `/update-location/${location.id}`;
    }
    if (location.validation === "bloqué") {
      return `/update-location/${location.id}`;
    }
    return null;
  };

  const editLink = getEditLink();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      whileHover={{ y: -2 }}
      className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm hover:shadow-xl border border-gray-200 dark:border-gray-700 transition-all duration-300 overflow-hidden group"
    >
      {/* Card Header with gradient */}
      <div className="h-1 bg-gradient-to-r from-emerald-500 via-blue-500 to-purple-500"></div>

      <div className="p-6">
        {/* Image and Basic Info */}
        <div className="flex items-start space-x-4 mb-4">
          <div className="flex-shrink-0">
            <div className="w-16 h-16 rounded-xl overflow-hidden shadow-md">
              <img
                className="w-full h-full object-cover"
                src={
                  location?.images && location?.images.length > 0
                    ? `${process.env.REACT_APP_CLOUD_FRONT}${location?.images[0]}`
                    : homeIcon
                }
                alt={location.title || "Espace"}
              />
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white truncate mb-2">
              {location.title || "Sans titre"}
            </h3>

            {/* Status Badge */}
            <div className="flex items-center space-x-2">
              <span
                className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${statusConfig.bgColor} ${statusConfig.color}`}
              >
                <StatusIcon className="w-4 h-4 mr-1.5" />
                {statusConfig.label}
              </span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-100 dark:border-gray-700">
          <div className="flex items-center space-x-2">
            {/* Edit/Complete Action */}
            {!isCustomer && editLink && location.validation !== "en cours" && (
              <Link to={editLink}>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="flex items-center space-x-1.5 px-3 py-2 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-all duration-200"
                >
                  <PencilIcon className="w-4 h-4" />
                  <span className="text-sm font-medium">
                    {location.validation === "brouillon" ? "Compléter" : "Corriger"}
                  </span>
                </motion.button>
              </Link>
            )}

            {/* Complete Documents Action for "en attente" */}
            {!isCustomer && location.validation === "en attente" && (
              <Link to={`/client/location/completed/${location.id}`}>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="flex items-center space-x-1.5 px-3 py-2 bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 rounded-lg hover:bg-orange-100 dark:hover:bg-orange-900/50 transition-all duration-200"
                >
                  <DocumentIcon className="w-4 h-4" />
                  <span className="text-sm font-medium">Compléter</span>
                </motion.button>
              </Link>
            )}

            {/* In Progress Indicator */}
            {location.validation === "en cours" && (
              <div className="flex items-center space-x-1.5 px-3 py-2 bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-lg">
                <CheckCircleIcon className="w-4 h-4" />
                <span className="text-sm font-medium">En cours</span>
              </div>
            )}

            {/* Availability Button for Approved Spaces */}
            {(location.validation === "approuvé" || location.validation === "Approuvé") && (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  setIsAvailabilityModalOpen(true);
                }}
                style={{
                  cursor: "pointer",
                  pointerEvents: "auto",
                }}
                className="flex items-center space-x-1.5 px-3 py-2 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 rounded-lg hover:bg-emerald-100 dark:hover:bg-emerald-900/50 transition-all duration-200"
              >
                <CalendarIcon className="w-4 h-4" />
                <span className="text-sm font-medium">Disponibilité</span>
              </motion.button>
            )}
          </div>

          {/* Delete Action */}
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => onDelete?.(location.id || "")}
            className="flex items-center space-x-1.5 px-3 py-2 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/50 transition-all duration-200"
          >
            <TrashIcon className="w-4 h-4" />
            <span className="text-sm font-medium">Supprimer</span>
          </motion.button>
        </div>
      </div>

      {/* Hover Effect Overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/5 via-blue-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>

      {/* Availability Modal */}
      <SimpleAvailabilityModal
        isOpen={isAvailabilityModalOpen}
        onClose={() => {
          console.log("Closing modal");
          setIsAvailabilityModalOpen(false);
        }}
        locationTitle={location.title || "Espace"}
      />
    </motion.div>
  );
};

export default ModernSpaceCard;
